import React from 'react';
import PropTypes from "prop-types";

import GoogleMap from "../GoogleMap";
import Marker from "../Marker";
import MapBounds from "../MapBounds";
import LiveTrackingContainer from "../LiveTrackingContainer";

import defaultSourceMarker from './source.svg';
import defaultDestinationMarker from './destination.svg';
import defaultDestinationReachedMarker from './destination-reached.svg';
import goSendPickupMarker from './go-send-pickup-marker.svg'
import goSendDropMarker from './go-send-drop-marker.svg'
import goSendDeliveredMarker from './go-send-delivered-marker.svg'
import {decodePolyline} from "../../google";
import GojekMap from '../GojekMap/gojekMap';
import { sendLoadBaseMapEvent } from '../../googleAnalytics';
import { ClickstreamContext } from '../../providers/Clickstream.provider';
import isRequiredIf from '../../utils/propValidators';

const markerPropType = PropTypes.shape({
  title: PropTypes.string,
  latitude: PropTypes.number,
  longitude: PropTypes.number,
});


const routePropType = PropTypes.shape({
  dynamic: PropTypes.shape({
    visibility: PropTypes.bool,
    polylines: PropTypes.arrayOf(PropTypes.string),
  }),
  static: PropTypes.shape({
    visibility: PropTypes.bool,
    polylines: PropTypes.arrayOf(PropTypes.string),
  }),
});

export default class TrackingMap extends React.Component {
  static propTypes = {
    serviceType: PropTypes.string.isRequired,
    countryCode: PropTypes.string,
    topCardHeight: PropTypes.number.isRequired,
    bottomCardHeight: PropTypes.number.isRequired,
    source: markerPropType.isRequired,
    destination: markerPropType.isRequired,
    driver: markerPropType,
    routePolyline: isRequiredIf((props) => props.serviceType !== 'go-car-rental', PropTypes.string),
    autoMapFocus: PropTypes.bool.isRequired,
    bookingComplete: PropTypes.bool.isRequired,
    route: routePropType,
    baseMaps: PropTypes.string.isRequired,
    status: PropTypes.string,
    isDesktopView: PropTypes.bool,
    isGoSendPartnerOrder: PropTypes.bool,
    isGoSendPartnerOrderOnHold: PropTypes.bool,
    snappedSegment: PropTypes.arrayOf(PropTypes.shape({
      latitude: PropTypes.number,
      longitude: PropTypes.number,
    })),
    bookingType: PropTypes.string,
    driverMarkerIcon: PropTypes.string,
  };

  static serviceTypeSourceMarkerMap = {
    "go-kilat-new": goSendPickupMarker,
    "sameday-kilat": goSendPickupMarker,
    "instant-mpmd": goSendPickupMarker,
    "go-send-instant-2w": goSendPickupMarker
  };

  static serviceTypeDestinationMarkerMap = {
    "go-kilat-new": goSendDropMarker,
    "sameday-kilat": goSendDropMarker,
    "instant-mpmd": goSendDropMarker,
    "go-send-instant-2w": goSendDropMarker
  };

  static serviceTypeDestinationReachedMarkerMap = {
    "go-kilat-new": goSendDeliveredMarker,
    "sameday-kilat": goSendDeliveredMarker,
    "instant-mpmd": goSendDeliveredMarker,
    "go-send-instant-2w": goSendDeliveredMarker
  };

  static contextType = ClickstreamContext;

  componentDidMount() {
    const clickstream = this.context;
    sendLoadBaseMapEvent(this.props.token, this.props.baseMaps);

    if (clickstream) {
      clickstream.track({
        eventName: "Map Load",
        baseMapLoad: {
          status: "loading",
          map: this.props.baseMaps,
          tripId: this.props.token,
        }
      });
    }
  }

  mapPoints() {
    var points = [];
    if (this.props.isGoSendPartnerOrderOnHold) {
      points.push({
        latitude: this.props.source.latitude,
        longitude: this.props.source.longitude,
      })
      points.push({
        latitude: this.props.destination.latitude,
        longitude: this.props.destination.longitude,
      })
      return points
    }

    if (this.props.status === "driver-found") {
      points.push({
        latitude: this.props.source.latitude,
        longitude: this.props.source.longitude,
      })
    }

    if (this.props.status === "picked-up" && !this.isEmptyDestination()) {
      points.push({
        latitude: this.props.destination.latitude,
        longitude: this.props.destination.longitude,
      })
    }

    if (this.props.status === "completed" || this.props.status === "created" || this.props.status === "rejected") {
      points.push({
        latitude: this.props.source.latitude,
        longitude: this.props.source.longitude,
      })

      if (!this.isEmptyDestination()) {
        points.push({
          latitude: this.props.destination.latitude,
          longitude: this.props.destination.longitude,
        })
      }
    }

    if (this.props.driver) {
      points.push(
        {
          latitude: this.props.driver.latitude,
          longitude: this.props.driver.longitude,
        },
      );
    }

    if (this.props.route && this.props.route.dynamic && this.props.route.dynamic.visibility && this.props.route.dynamic.polylines.length > 0) {
      const encodedPath = this.props.route.dynamic.polylines[0];
      const polylineCoordinates = decodePolyline(encodedPath);
      const polylineCoordinatesWithFormattedKeys = polylineCoordinates.map(coordinate => {
        return {
          latitude: coordinate.lat(),
          longitude: coordinate.lng()
        }
      })
      points = points.concat(polylineCoordinatesWithFormattedKeys)
    }

    return points;
  }

  boundsPadding() {
    return {
      bottom: this.props.isDesktopView ? 100 : this.props.bottomCardHeight,
      left: this.props.isDesktopView ? 400 : 0,
      right: 0,
      top: this.props.isDesktopView ? 100 : this.props.topCardHeight,
    };
  }

  boundsPaddingForGojekMap() {
    return {
      bottom: this.props.isDesktopView ? 130 : this.props.bottomCardHeight + 30,
      left: this.props.isDesktopView ? 450 : 50,
      right: 50,
      top: this.props.isDesktopView ? 130 : this.props.topCardHeight + 30
    };
  }

  getSourceMarker() {
    let sourceMarker = TrackingMap.serviceTypeSourceMarkerMap[this.props.serviceType];
    return sourceMarker ? sourceMarker : defaultSourceMarker;
  }

  getDestinationMarker() {
    let destinationMarker = TrackingMap.serviceTypeDestinationMarkerMap[this.props.serviceType];
    destinationMarker = destinationMarker ? destinationMarker : defaultDestinationMarker;
    return this.props.bookingComplete ? this.getDestinationReachedMarker() : destinationMarker;
  }

  getDestinationReachedMarker() {
    let destinationReachedMarker = TrackingMap.serviceTypeDestinationReachedMarkerMap[this.props.serviceType];
    return destinationReachedMarker ? destinationReachedMarker : defaultDestinationReachedMarker;
  }

  isEmptyDestination() {
    return !(this.props.destination && this.props.destination.latitude && this.props.destination.longitude);
  }

  render() {
    let map;
    const onBoundsChange = (evt) => this.props.onBoundsChange(evt);
    if(this.props.baseMaps === "gojek") {
      map = <GojekMap 
              {...this.props} 
              token={this.props.token}
              onBoundsChange={onBoundsChange}
              padding={this.boundsPaddingForGojekMap()} 
              points={this.mapPoints()}
              autoMapFocus={this.props.autoMapFocus}
              isDesktopView={this.props.isDesktopView}
            />
    } else {
      map = <GoogleMap token={this.props.token} onBoundsChange={onBoundsChange} isGoSendPartnerOrder={this.props.isGoSendPartnerOrder}>
        <MapBounds autoMapFocus={this.props.autoMapFocus} points={this.mapPoints()} padding={this.boundsPadding()}/>
        <Marker title={this.props.source.title} latitude={this.props.source.latitude} longitude={this.props.source.longitude} iconURL={this.getSourceMarker()}/>
        {!this.isEmptyDestination() &&
          <Marker title={this.props.destination.title} latitude={this.props.destination.latitude} longitude={this.props.destination.longitude} iconURL={this.getDestinationMarker()} />}
        <LiveTrackingContainer
          countryCode={this.props.countryCode}
          serviceType={this.props.serviceType}
          driverLocation={this.props.driver}
          route={this.props.route}
          snappedSegment={this.props.snappedSegment}
          bookingType={this.props.bookingType}
          isGoSendPartnerOrder={this.props.isGoSendPartnerOrder}
          driverMarkerIcon={this.props.driverMarkerIcon}
        />
      </GoogleMap>
    }
    return (map);
  }
}
